// src/api.js
import axios from "axios";
import { AUTH_NOTICE_KEY } from "./utils/sessionManager";

const API = axios.create({
  baseURL: "/api",
});

// Helper to clear all auth data (avoiding circular dependency with auth.js)
function clearAllAuthData() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("role"); // legacy
  localStorage.removeItem("activeRole"); // selected role
  localStorage.removeItem("roles"); // cached roles from /me

  // Destroy session tracking if sessionManager exists
  try {
    const sessionManager = require("./utils/sessionManager").default;
    if (sessionManager && typeof sessionManager.destroy === "function") {
      sessionManager.destroy();
    }
  } catch (e) {
    // sessionManager might not be available, ignore
  }
}

// ✅ Attach JWT before every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ✅ HANDLE EXPIRED / INVALID TOKEN GLOBALLY
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const message =
      error?.response?.data?.message || error?.response?.data?.error || "";

    // 🔴 CHECK IF BACKEND IS DOWN (Network Error)
    if (
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      !error.response
    ) {
      // Show backend down indicator
      showBackendDownBanner();
      return Promise.reject(error);
    }

    // 🔴 LICENSE INVALID — redirect to the license page
    if (status === 402 || error?.response?.data?.licenseInvalid === true) {
      if (!window.location.pathname.includes("/license-invalid")) {
        window.location.href = "/license-invalid";
      }
      return Promise.reject(error);
    }

    const isAuthError =
      status === 401 ||
      (status === 403 &&
        (message.toLowerCase().includes("jwt") ||
          message.toLowerCase().includes("token") ||
          message.toLowerCase().includes("authentication")));

    if (isAuthError) {
      const notice =
        message ||
        (status === 401
          ? "Your session has expired. Please log in again."
          : "Your session is no longer valid. Please log in again.");
      sessionStorage.setItem(AUTH_NOTICE_KEY, notice);

      // Clear all auth data including roles, activeRole, and session tracking
      clearAllAuthData();

      if (!window.location.pathname.includes("/login")) {
        window.location.replace("/login");
      }
    }

    return Promise.reject(error);
  },
);

// 🔴 Backend Down Banner
let backendDownBannerShown = false;
function showBackendDownBanner() {
  if (backendDownBannerShown) return;
  backendDownBannerShown = true;

  // Create banner
  const banner = document.createElement("div");
  banner.id = "backend-down-banner";
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    padding: 16px 24px;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    z-index: 99999;
    animation: slideDown 0.4s ease-out;
  `;
  banner.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; gap: 12px;">
      <svg style="width: 24px; height: 24px;" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span>⚠️ Backend Server is Down - Please contact administrator or try again later</span>
    </div>
  `;

  // Add animation
  const style = document.createElement("style");
  style.textContent = `
    @keyframes slideDown {
      from { transform: translateY(-100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  document.body.prepend(banner);

  // Adjust body padding to account for banner
  document.body.style.paddingTop = "64px";
}

export default API;
